import { HttpErrorResponse } from '@angular/common/http';
import {
  DashboardDateRange,
  DashboardFilters,
  DashboardScope,
} from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import { DashboardVm, DashboardVmState } from '@partner/feature-modules/dashboard/domain/model/dashboard-vm.model';

const HTTP_BAD_REQUEST = 400;

/** What one dashboard request is keyed by; the period only labels the result and never triggers a fetch on its own. */
export interface DashboardSelection {
  range: DashboardDateRange;
  scope: DashboardScope;
}

export function sameSelection(previous: DashboardSelection, current: DashboardSelection): boolean {
  return (
    previous.range.from === current.range.from &&
    previous.range.to === current.range.to &&
    previous.scope === current.scope
  );
}

export function toSelection([filters, range]: [
  DashboardFilters,
  DashboardDateRange | null,
]): DashboardSelection | null {
  return range ? { range, scope: filters.scope } : null;
}

/** Server time never shown as being in the future; null for an unparsable instant. */
export function clampToNow(asOfUtc: Date, now: Date): Date | null {
  if (!(asOfUtc instanceof Date) || isNaN(asOfUtc.getTime())) {
    return null;
  }
  return asOfUtc.getTime() > now.getTime() ? now : asOfUtc;
}

/** The 400 body's `Message` (the validator text); anything else renders the generic state. */
export function serverMessage(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== HTTP_BAD_REQUEST) {
    return null;
  }
  const body = error.error as { Message?: unknown; message?: unknown } | null;
  const message = body?.Message ?? body?.message;
  return typeof message === 'string' && message.trim() ? message : null;
}

export function loadingVm<T>(): DashboardVm<T> {
  return { state: DashboardVmState.LOADING, asOfUtc: null, data: null, errorMessage: null };
}

export function errorVm<T>(error: unknown): DashboardVm<T> {
  return { state: DashboardVmState.ERROR, asOfUtc: null, data: null, errorMessage: serverMessage(error) };
}
